import { chain, filter, isEmpty, isNil, keyBy } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Inject, Injectable } from '../../injection';
import { EmptyResponse } from '../data/empty-response.do';
import { MobizClientPluginConfiguration } from '../data/mobiz-client-plugin-configuration.do';
import { MobizClientPluginInfo } from '../data/mobiz-client-plugin-info.do';
import { OrganizationMembership } from '../data/organization-membership.do';
import { Organization, OrganizationEagerLoad } from '../data/organization.do';
import { PagedResults } from '../data/paged-results.model';
import { SerializableObjectList } from '../data/serializable-object-list.model';
import { ApiEndpointConfiguration } from '../interfaces/api-endpoint-configuration.interface';
import { Dictionary } from '../interfaces/dictionary.interface';
import { IMobizHttpService } from '../interfaces/mobiz-http.interface';
import { FlagsEnum } from '../other/flags-enum';
import { MobizServerApi } from '../other/mobiz-server-api.enum';

import { IMobizExtensionService, MobizClientPluginConfigurationQueryParameters } from './mobiz-extension.interface';
import { IOrganizationService } from './organization.interface';
import { IServerApiBuilderService } from './server-api-builder.interface';

@Injectable()
export class MobizExtensionService implements IMobizExtensionService {

  constructor(
    @Inject(IMobizHttpService)
    private http: IMobizHttpService,
    @Inject(IServerApiBuilderService)
    private apiBuilder: IServerApiBuilderService,
    @Inject(IOrganizationService)
    private organizationService: IOrganizationService,
  ) {
  }

  public getAllClientPlugins(): Observable<MobizClientPluginInfo[]> {
    let config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.ClientPluginInfo,
    };
    let extensions = new SerializableObjectList<MobizClientPluginInfo>(MobizClientPluginInfo);
    return this.http.get(extensions, config)
      .pipe(map((response: SerializableObjectList<MobizClientPluginInfo>) => {
        return filter(response.items, item => !isEmpty(item.bootstrapFile));
      }));
  }

  public getEnabledClientPlugins(membership?: OrganizationMembership): Observable<MobizClientPluginInfo[]> {
    return combineLatest([
      this.getAllClientPlugins(),
      this.getConfigurations(membership),
    ]).pipe(
      map(([plugins, configurations]: [MobizClientPluginInfo[], MobizClientPluginConfiguration[]])
        : MobizClientPluginInfo[] => {
        return this.filterEnabledClientPlugins(plugins, configurations);
      }),
    );
  }

  private getConfigurations(membership?: OrganizationMembership): Observable<MobizClientPluginConfiguration[]> {
    if (isNil(membership)) {
      const config: ApiEndpointConfiguration = {
        serverApi: MobizServerApi.ClientPluginConfigurationForMobizAdministrator,
      };
      let configurations = new SerializableObjectList<MobizClientPluginConfiguration>(MobizClientPluginConfiguration);
      return this.http.get(configurations, config)
        .pipe(map((response: SerializableObjectList<MobizClientPluginConfiguration>) => {
          return response.items;
        }));
    }

    const eagerLoad = new FlagsEnum<OrganizationEagerLoad>();
    eagerLoad.add(OrganizationEagerLoad.ClientPluginConfigurations);
    return this.organizationService.getOrganization(membership.organizationId, eagerLoad)
      .pipe(map((organization: Organization) => {
        return organization.clientPluginConfigurations;
      }));
  }

  private filterEnabledClientPlugins(plugins: MobizClientPluginInfo[], configurations: MobizClientPluginConfiguration[])
    : MobizClientPluginInfo[] {
    const pluginsMap: Dictionary<MobizClientPluginInfo> = keyBy(plugins, p => p.key);
    return chain(configurations)
      .filter(c => c.enabled)
      .map(c => pluginsMap[c.clientPluginKey])
      .filter(p => !isNil(p))
      .value();
  }

  public getBootstrapFileUrl(clientPlugin: MobizClientPluginInfo): string {
    const urlParameters = {
      extensionKey: clientPlugin.extensionKey,
      clientPluginKey: clientPlugin.key,
      file: encodeURIComponent(clientPlugin.bootstrapFile),
    };
    return this.apiBuilder.getUrl(MobizServerApi.ClientPluginFile, urlParameters, {});
  }

  public createConfiguration(configuration: MobizClientPluginConfiguration): Observable<MobizClientPluginConfiguration> {
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.ClientPluginConfiguration,
      body: JSON.stringify(configuration),
    };
    return this.http.post(new MobizClientPluginConfiguration(), config);
  }

  public getAllConfigurations(queryParameters: MobizClientPluginConfigurationQueryParameters):
    Observable<PagedResults<MobizClientPluginConfiguration>> {
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.ClientPluginConfiguration,
      queryParameters: queryParameters,
    };
    const pagedConfiguration = new PagedResults<MobizClientPluginConfiguration>(MobizClientPluginConfiguration);
    return this.http.get(pagedConfiguration, config);
  }

  public getConfiguration(configurationId: string): Observable<MobizClientPluginConfiguration> {
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.ClientPluginConfigurationResource,
      urlParameters: {
        configurationId: configurationId,
      },
    };
    return this.http.get(new MobizClientPluginConfiguration(), config);
  }

  public updateConfiguration(configuration: MobizClientPluginConfiguration): Observable<MobizClientPluginConfiguration> {
    const config: ApiEndpointConfiguration = {
      body: JSON.stringify(configuration),
      serverApi: MobizServerApi.ClientPluginConfigurationResource,
      urlParameters: {
        configurationId: configuration.id,
      },
    };
    return this.http.put(new MobizClientPluginConfiguration(), config);
  }

  public deleteConfiguration(configurationId: string): Observable<EmptyResponse> {
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.ClientPluginConfigurationResource,
      urlParameters: {
        configurationId: configurationId,
      },
    };
    return this.http.delete(new EmptyResponse(), config);
  }

}
